import { offerApiService } from '../services/offerApi';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CreditProductType,
  OceanCreditProductName,
  type CreditOffer,
  type GenericOffer,
  type LoanOfferState,
  type PreliminaryOfferResponse,
} from '../types/offer';
import { useMemo, useRef } from 'react';

export const LoanOfferQueryKeys = {
  loanOfferState: 'loan-offer-state',
};

export const CREDIT_LINE_MIN_PAYMENT_PERCENTAGE = 0.05;
export const AMOUNT_SLIDER_STEP = 100;

export type GenericSubproductOffer = {
  id: string;
  subproduct: string;
  availableBalance: number;
};

const defaultGenericSubproductOffers: GenericSubproductOffer[] = [
  { id: 'volt-zero-generic', subproduct: 'volt_zero', availableBalance: 250 },
  { id: 'volt-one-generic', subproduct: 'volt_one', availableBalance: 5000 },
  { id: 'volt-infinite-generic', subproduct: 'volt_infinite', availableBalance: 50000 },
];

const defaultGenericOffers = [
  {
    maxAmount: 12000,
    creditProductName: OceanCreditProductName.DolphinPlus,
    minAmount: 100,
    durationMonths: 24,
    type: CreditProductType.WhiteLabelCard,
  },
  {
    maxAmount: 12000,
    creditProductName: OceanCreditProductName.Penguin,
    minAmount: 100,
    durationMonths: 24,
    type: CreditProductType.Consumer,
  },
] as GenericOffer[];

interface UseLoanOfferReturn {
  getLoanOffersState: () => Promise<LoanOfferState | undefined>;
  uploadProofOfIncome: (file: File) => Promise<void>;
  requestPreliminaryOffer: () => Promise<PreliminaryOfferResponse>;
  isUploadingProofOfIncome: boolean;
  creditLineOffer: CreditOffer | undefined;
  hasCreditLineOffer: boolean;
  installmentOffer: CreditOffer | undefined;
  hasInstallmentOffer: boolean;
  genericSubproductOffers: GenericSubproductOffer[];
  isLoading: boolean;
  loanOfferState: LoanOfferState | undefined;
  isOfferExpired: boolean;
  genericOffers: GenericOffer[];
  proofOfIncomeRequired: boolean;
  proofOfIncomeProcessing: boolean;
}

export function useLoanOffer(fetchOffer: boolean = false): UseLoanOfferReturn {
  const queryClient = useQueryClient();

  const loanOfferStateQuery = useQuery({
    queryKey: [LoanOfferQueryKeys.loanOfferState],
    queryFn: () => offerApiService.getLoanOfferState(),
    enabled: fetchOffer,
  });

  const uploadProofOfIncomeMutation = useMutation({
    mutationFn: (file: File) => offerApiService.uploadProofOfIncome(file),
    // Done once the offers state is read again: it tells the review began.
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [LoanOfferQueryKeys.loanOfferState],
        refetchType: 'all',
      }),
  });

  const requestPreliminaryOfferMutation = useMutation({
    mutationFn: () => offerApiService.requestPreliminaryOffer(),
  });

  // A repeated call while one is in flight (StrictMode re-running the effect,
  // a remount) shares it instead of sending a second POST.
  const inFlightPreliminaryOffer =
    useRef<Promise<PreliminaryOfferResponse> | null>(null);

  const requestPreliminaryOffer = () => {
    if (inFlightPreliminaryOffer.current) {
      return inFlightPreliminaryOffer.current;
    }

    const promise = requestPreliminaryOfferMutation
      .mutateAsync()
      .finally(() => {
        inFlightPreliminaryOffer.current = null;
      });
    inFlightPreliminaryOffer.current = promise;
    return promise;
  };

  const getLoanOffersState = async () => {
    const result = await loanOfferStateQuery.refetch();
    return result.data;
  };

  const creditLineOffer = useMemo(() => {
    return (
      loanOfferStateQuery.data?.creditOffers?.find(
        (offer) =>
          offer.type === CreditProductType.WhiteLabelCard &&
          offer.isAvailable &&
          offer.isFinal !== false
      ) ?? undefined
    );
  }, [loanOfferStateQuery.data]);

  const installmentOffer = useMemo(() => {
    return (
      loanOfferStateQuery.data?.creditOffers?.find(
        (offer) =>
          offer.type === CreditProductType.Consumer &&
          offer.isAvailable &&
          offer.isFinal !== false
      ) ?? undefined
    );
  }, [loanOfferStateQuery.data]);

  return {
    getLoanOffersState,
    uploadProofOfIncome: uploadProofOfIncomeMutation.mutateAsync,
    requestPreliminaryOffer,
    isUploadingProofOfIncome: uploadProofOfIncomeMutation.isPending,
    creditLineOffer,
    installmentOffer,
    hasCreditLineOffer: !!creditLineOffer,
    hasInstallmentOffer: !!installmentOffer,
    isLoading: loanOfferStateQuery.isLoading,
    loanOfferState: loanOfferStateQuery.data,
    isOfferExpired: loanOfferStateQuery.data?.expiresAt
      ? new Date(loanOfferStateQuery.data.expiresAt) < new Date()
      : true,
    genericSubproductOffers: defaultGenericSubproductOffers,
    genericOffers:
      loanOfferStateQuery.data?.genericOffers ?? defaultGenericOffers,
    proofOfIncomeRequired: loanOfferStateQuery.data?.proofOfIncomeRequired ?? false,
    proofOfIncomeProcessing: loanOfferStateQuery.data?.proofOfIncomeProcessing ?? false,
  };
}
